// Massekatalogen (settings/massCatalog): standardverdier, et lite lager som lytteren i
// useMassCatalogSync fyller, og rene hjelpefunksjoner som leser lageret.
// Standarden speiler functions/src/massCatalogDefaults.js. Endre begge samtidig.
import { useSyncExternalStore } from 'react';

const opt = (key, label, order, hint = '') => ({ key, label, active: true, order, hint });

// Hjelpetekstene (bruksområde) er utkast som må godkjennes. Admin kan endre dem i massekatalogen.
export const DEFAULT_MASS_CATALOG = {
  version: 0,
  types: [
    {
      key: 'soil', label: 'Jord', icon: 'layers', active: true, order: 1, priceFactor: 1.0,
      defaultEquipment: ['excavator'], supportsBags: false, subLabel: 'Jordkvalitet', subRequired: true,
      hint: 'Til hager, plener og terrengforming. Velg kvalitet under.',
      options: [
        opt('clean', 'Ren jord', 1, 'Jord uten forurensning. Til fyll og grøntarealer.'),
        opt('topsoil', 'Matjord', 2, 'Næringsrikt topplag til plener, bed og dyrking.'),
        opt('clay', 'Leire', 3, 'Tett masse. Brukes i fyllinger og som tetting.'),
        opt('fill', 'Fyllmasse', 4, 'Til oppfylling og planering under bygg og plasser.'),
        opt('contaminated', 'Forurenset jord', 5, 'Må leveres til godkjent mottak.'),
      ],
    },
    {
      key: 'gravel', label: 'Grus', icon: 'pile', active: true, order: 2, priceFactor: 1.1,
      defaultEquipment: ['wheel_loader'], supportsBags: true, subLabel: '', subRequired: false,
      hint: 'Bærelag og planering under veier, plasser og innkjørsler.', options: [],
    },
    {
      key: 'crushed_stone', label: 'Pukk', icon: 'pile', active: true, order: 3, priceFactor: 1.2,
      defaultEquipment: ['wheel_loader'], supportsBags: true, subLabel: 'Kornstørrelse', subRequired: true,
      hint: 'Knust stein i ulike kornstørrelser. Velg etter bruk.',
      options: [
        opt('0-4mm', '0–4 mm', 1, 'Settlag under belegningsstein og til fuging.'),
        opt('4-8mm', '4–8 mm', 2, 'Drenering, fuging og dekke på stier.'),
        opt('8-16mm', '8–16 mm', 3, 'Drenering rundt rør og i grøfter, og dekke på stier.'),
        opt('16-22mm', '16–22 mm', 4, 'Drenering og grøftefylling.'),
        opt('22-32mm', '22–32 mm', 5, 'Bærelag og drenering under plasser og veier.'),
        opt('32-64mm', '32–64 mm', 6, 'Forsterkningslag og drenering under veier og plasser.'),
        opt('64mm+', '64 mm+', 7, 'Grov stein til erosjonssikring og fylling.'),
      ],
    },
    {
      key: 'sand', label: 'Sand', icon: 'pile', active: true, order: 4, priceFactor: 1.0,
      defaultEquipment: ['wheel_loader'], supportsBags: true, subLabel: '', subRequired: false,
      hint: 'Settlag under belegningsstein, fuging og utjevning.', options: [],
    },
    {
      key: 'other', label: 'Annet', icon: 'plus', active: true, order: 5, priceFactor: 1.0,
      defaultEquipment: [], supportsBags: false, subLabel: '', subRequired: false, hint: '', options: [],
    },
  ],
};

// En lagret katalog uten hint får standardtekstene. Er hint satt (også tomt), brukes det som står.
function withDefaultHints(catalog) {
  return {
    ...catalog,
    types: catalog.types.map((t) => {
      const dt = DEFAULT_MASS_CATALOG.types.find((d) => d.key === t.key);
      return {
        ...t,
        hint: typeof t.hint === 'string' ? t.hint : (dt?.hint ?? ''),
        options: (t.options ?? []).map((o) => {
          const dopt = dt?.options.find((d) => d.key === o.key);
          return { ...o, hint: typeof o.hint === 'string' ? o.hint : (dopt?.hint ?? '') };
        }),
      };
    }),
  };
}

// ── Lager ──────────────────────────────────────────────────────────────────

let current = DEFAULT_MASS_CATALOG;
const listeners = new Set();

const isUsable = (c) => Array.isArray(c?.types) && c.types.length > 0
  && c.types.every((t) => typeof t?.key === 'string' && typeof t?.label === 'string');

export const getMassCatalog = () => current;

// Ugyldig eller manglende katalog gir standarden.
export function setMassCatalog(next) {
  current = isUsable(next)
    ? withDefaultHints({ ...next, version: Number.isInteger(next.version) ? next.version : 0 })
    : DEFAULT_MASS_CATALOG;
  listeners.forEach((l) => l());
}

export function subscribeMassCatalog(listener) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

// For komponenter som skal tegnes på nytt når admin endrer katalogen.
export const useMassCatalog = () => useSyncExternalStore(subscribeMassCatalog, getMassCatalog);

// ── Hjelpefunksjoner (leser lageret hvis katalog ikke oppgis) ─────────────

const byOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0);

export const findMassType = (key, c = current) => c.types.find((t) => t.key === key) ?? null;
export const activeMassTypes = (c = current) => c.types.filter((t) => t.active).sort(byOrder);
export const activeOptions = (type) => (type?.options ?? []).filter((o) => o.active).sort(byOrder);

// Massetypen som bortkjøring lagres på. Finnes i katalogen i Firestore, ikke i standardkatalogen.
export const REMOVAL_MASS_TYPE = 'bortkjoring';

// «removal» er den gamle massetypen for bortkjøring og finnes ikke i katalogen.
export function massLabel(key, c = current) {
  if (key === 'removal') return 'Bortkjøring';
  return findMassType(key, c)?.label ?? key;
}

export const massFactor = (key, c = current) => findMassType(key, c)?.priceFactor ?? 1.0;
export const massSupportsBags = (key, c = current) => findMassType(key, c)?.supportsBags === true;
export const massDefaultEquipment = (key, c = current) => findMassType(key, c)?.defaultEquipment ?? [];

// Valgt undervalg på et oppdrag. Eldre oppdrag har jord og pukk i hvert sitt felt.
export const massOptionOf = (order) => order?.massOption ?? order?.soilQuality ?? order?.crushedStoneGrade ?? null;

export function optionLabel(typeKey, optionKey, c = current) {
  return findMassType(typeKey, c)?.options.find((o) => o.key === optionKey)?.label ?? optionKey;
}

export const typeHint = (key, c = current) => findMassType(key, c)?.hint ?? '';

export function optionHint(typeKey, optionKey, c = current) {
  return findMassType(typeKey, c)?.options.find((o) => o.key === optionKey)?.hint ?? '';
}

// Samme regler som functions/src/massCatalog.js (som er fasit). Gir rask tilbakemelding i adminsiden.
export function validateCatalogDraft(catalog) {
  const errors = [];
  const types = catalog?.types ?? [];
  const typeKeys = new Set();
  types.forEach((t, i) => {
    const label = (t.label ?? '').trim();
    const name = label || `Massetype ${i + 1}`;
    if (typeKeys.has(t.key)) errors.push(`${name}: nøkkelen «${t.key}» er brukt flere ganger`);
    typeKeys.add(t.key);
    if (!label || label.length > 40) errors.push(`${name}: navn må være 1–40 tegn`);
    if (!(t.priceFactor >= 0.5 && t.priceFactor <= 3)) errors.push(`${name}: prisfaktor må være mellom 0,5 og 3`);
    if ((t.subLabel ?? '').length > 30) errors.push(`${name}: etiketten for undervalg kan ha maks 30 tegn`);
    if ((t.hint ?? '').length > 120) errors.push(`${name}: hjelpetekst kan ha maks 120 tegn`);
    const optionKeys = new Set();
    (t.options ?? []).forEach((o, j) => {
      const oName = `${name}, valg ${j + 1}`;
      if (optionKeys.has(o.key)) errors.push(`${oName}: nøkkelen «${o.key}» er brukt flere ganger`);
      optionKeys.add(o.key);
      const oLabel = (o.label ?? '').trim();
      if (!oLabel || oLabel.length > 40) errors.push(`${oName}: navn må være 1–40 tegn`);
      if ((o.hint ?? '').length > 120) errors.push(`${oName}: hjelpetekst kan ha maks 120 tegn`);
    });
    if (t.active && t.subRequired && !(t.options ?? []).some((o) => o.active)) {
      errors.push(`${name}: påkrevd undervalg trenger minst ett aktivt valg`);
    }
  });
  if (!types.some((t) => t.active)) errors.push('Minst én massetype må være aktiv');
  return errors;
}
